/**
 * CPU-side animation state (pure, tested): the first-touch unfold spring, per-ring ornament rotation
 * (slow alternating drift, prayer-wheel spin-up on latch), the 20 s melt, and the one-shot pulses.
 * All values are advanced from `frame(t)` and uploaded as uniforms; nothing here allocates per frame.
 */
import type { Family } from '@tonist/shared';

export const TAU = Math.PI * 2;
export const MAX_PETALS = 16;

/** Under-damped spring response 0→1 (ζ = 0.8) that settles within `settleSec`; 0 before t = 0. */
export function springStep(tSec: number, settleSec = 0.7, zeta = 0.8): number {
  if (tSec <= 0) return 0;
  // ζω·settle ≈ 5.3 gives ~1 % residual at settleSec
  const omega = 5.3 / (zeta * settleSec);
  const wd = omega * Math.sqrt(1 - zeta * zeta);
  const e = Math.exp(-zeta * omega * tSec);
  return 1 - e * (Math.cos(wd * tSec) + (zeta / Math.sqrt(1 - zeta * zeta)) * Math.sin(wd * tSec));
}

/** Petal unfold: spring 0→1 over 700 ms with a 40 ms stagger per petal (PLAN §4.2 moment 1). */
export class PetalUnfold {
  readonly open = new Float32Array(MAX_PETALS);
  private startSec = -1;
  private forced = false;
  /** Whether a first touch has happened. */
  get started(): boolean { return this.forced || this.startSec >= 0; }
  begin(nowSec: number): void { if (!this.started) this.startSec = nowSec; }
  /** Skip the animation (e.g. calm mode / reduced motion): everything open. */
  forceOpen(): void { this.forced = true; }
  update(nowSec: number): void {
    for (let i = 0; i < MAX_PETALS; i++) {
      this.open[i] = this.forced ? 1 : this.startSec < 0 ? 0 : springStep(nowSec - this.startSec - i * 0.04, 0.7);
    }
  }
}

/** Base ornament drift per ring in rpm: alternating direction, phase-offset so nothing pulses in unison (≤ 2 rpm). */
export const RING_DRIFT_RPM: readonly number[] = [0.35, -0.5, 0.65, -0.8, 0.3];
/** Prayer-wheel speed for a latched ring (PLAN §4.2 moment 2). */
export const LATCH_RPM = 1.0;
/** Melt reaches 0.15 after 20 s of a continuous / latched voice in the ring. */
export const MELT_MAX = 0.15;
export const MELT_SECONDS = 20;

export const rpmToRadPerSec = (rpm: number): number => (rpm * TAU) / 60;

/** Prayer-wheel direction by family: east blue / north green / white turn counter-clockwise (+), west red / south yellow clockwise (−). */
export function spinDirection(family: Family): 1 | -1 {
  return family === 'red' || family === 'yellow' ? -1 : 1;
}

export interface RingActivityInput {
  /** whether ring k currently has a latched (or continuous-mode sounding) voice */
  latched: boolean;
  family: Family;
}

export class RingMotion {
  /** ornament rotation angle per ring R0..R4 (radians, CCW positive) */
  readonly angle = new Float32Array(5);
  /** current angular velocity per ring (rad/s) */
  readonly velocity = new Float32Array(5);
  /** melt amount per ring 0..MELT_MAX */
  readonly melt = new Float32Array(5);
  /** external spin of the fire ring (prayer-wheel gesture), rad/s; decays like a wheel */
  private wheel = 0;
  /** extra kick from a latch pulse, decays */
  private kick = 0;
  private kickSign: 1 | -1 = 1;
  private lastLatched = [false, false, false, false, false];

  constructor() {
    for (let k = 0; k < 5; k++) this.velocity[k] = rpmToRadPerSec(RING_DRIFT_RPM[k]!);
  }

  /** 'latch' spins the ring up like a prayer wheel; 'unlatch' lets it coast down. */
  pulse(kind: 'latch' | 'unlatch'): void {
    if (kind === 'latch') { this.kick = rpmToRadPerSec(LATCH_RPM) * 3; }
    else { this.kick = 0; }
  }

  /** Circular motion in the fire ring: set the wheel's angular velocity (rad/s, CCW positive). */
  setWheel(omegaRadPerSec: number): void {
    this.wheel = Number.isFinite(omegaRadPerSec) ? Math.max(-4, Math.min(4, omegaRadPerSec)) : 0;
  }

  update(dtSec: number, rings: readonly RingActivityInput[], calm: boolean): void {
    const dt = Math.min(0.1, Math.max(0, dtSec));
    let anyLatched = false;
    // the wheel coasts down with a ~3 s time constant when the finger leaves
    this.wheel *= Math.exp(-dt / 3);
    for (let k = 0; k < 5; k++) {
      const r = rings[k];
      const latched = !!r?.latched;
      if (latched) anyLatched = true;
      const dir = r ? spinDirection(r.family) : 1;
      if (latched && !this.lastLatched[k]) this.kickSign = dir;
      this.lastLatched[k] = latched;
      let target = calm ? 0 : latched ? dir * rpmToRadPerSec(LATCH_RPM) + this.kickSign * this.kick : rpmToRadPerSec(RING_DRIFT_RPM[k]!);
      if (k === 4 && !calm) target += this.wheel;
      // slow liquid approach to the target speed (~1.5 s)
      const a = 1 - Math.exp(-dt / 1.5);
      this.velocity[k] = this.velocity[k]! + (target - this.velocity[k]!) * a;
      if (!calm) this.angle[k] = (this.angle[k]! + this.velocity[k]! * dt) % TAU;
      // melt: rises linearly to MELT_MAX over 20 s while latched, relaxes over ~5 s otherwise
      if (latched && !calm) this.melt[k] = Math.min(MELT_MAX, this.melt[k]! + (MELT_MAX / MELT_SECONDS) * dt);
      else this.melt[k] = Math.max(0, this.melt[k]! - (MELT_MAX / 5) * dt);
    }
    if (!anyLatched) this.kick = 0;
    this.kick *= Math.exp(-dt / 1.2);
  }
}

/** Linear progress 0..1 for a one-shot of `durationSec`, or -1 when idle. */
export class OneShot {
  private start = -1;
  constructor(readonly durationSec: number) {}
  trigger(nowSec: number): void { this.start = nowSec; }
  progress(nowSec: number): number {
    if (this.start < 0) return -1;
    const p = (nowSec - this.start) / this.durationSec;
    if (p >= 1) { this.start = -1; return -1; }
    return Math.max(0, p);
  }
  get active(): boolean { return this.start >= 0; }
}

/** Critically damped first-order smoothing toward `target` with time constant `tauSec`. */
export function smoothTowards(current: number, target: number, dtSec: number, tauSec: number): number {
  if (tauSec <= 0) return target;
  return current + (target - current) * (1 - Math.exp(-dtSec / tauSec));
}

/** Analytic calm-mode ripple ring events (x, y, t0, strength), fixed capacity. */
export class CalmRings {
  static readonly MAX = 8;
  readonly data = new Float32Array(CalmRings.MAX * 4);
  count = 0;
  private next = 0;
  add(x: number, y: number, tSec: number, strength: number): void {
    const o = this.next * 4;
    this.data[o] = x; this.data[o + 1] = y; this.data[o + 2] = tSec; this.data[o + 3] = strength;
    this.next = (this.next + 1) % CalmRings.MAX;
    this.count = Math.min(CalmRings.MAX, this.count + 1);
  }
}
